import { createFileRoute } from "@tanstack/react-router";
import { useOrgMe, useSignalDefinitions, useSignals, useSummary, useWork } from "../api/hooks";
import { SummaryCard } from "../components/summary-card";
import { Chip, Empty, ErrorBox, Loading, Page, Table, statusTone } from "../components/ui";
import { SignalCard } from "../components/signal-card";
import { latestByScope, toneFor, unitOf } from "../lib/signals";
import { title } from "../lib/format";

export const Route = createFileRoute("/team")({ component: Team });

const teamKeys = ["meeting_hours_week", "after_hours_pct", "one_on_one_coverage_pct", "messages_per_day"];
// The work-facing person keys; Team members shows the full set per report.
const personKeys = ["open_work_items", "overdue_work_items", "items_closed_week", "cycle_time_days", "one_on_one_completion_pct", "goal_health_pct"];

function Team() {
  const org = useOrgMe();
  const defs = useSignalDefinitions();
  const meKey = org.data?.me?.person_key;
  const teamSignals = useSignals({ scope_type: "manager", scope_key: meKey ?? "-" });
  const personSignals = useSignals({ scope_type: "person" });
  const work = useWork({ open_only: true, limit: 200 });
  const summary = useSummary("manager", meKey);
  if (org.isPending) return <Page title="Team health"><Loading /></Page>;
  if (org.isError) return <Page title="Team health"><ErrorBox error={org.error} /></Page>;
  if (!org.data.me) return <Page title="Team health"><Empty title="Your user is not linked to a person" body="Ask an admin to link your sign-in to your HRIS record; then your team appears here." /></Page>;
  const team = teamSignals.data ? latestByScope(teamSignals.data) : new Map();
  const people = personSignals.data ? latestByScope(personSignals.data) : new Map();
  const reports = org.data.direct_reports;
  return (
    <Page title="Team health" sub={`${org.data.me.full_name ?? "You"} · ${reports.length} direct reports · ${org.data.line_size} in your line`}>
      <div className="mb-3"><SummaryCard summary={summary.data} pending={summary.isPending} title="Your team, in plain words" /></div>
      <div className="grid grid-cols-4 gap-3">
        {teamKeys.map((k) => (
          <SignalCard key={k} label={title(k).replace(" Pct", " %")} unit={unitOf(defs.data, k)} entry={meKey ? team.get(`${k}|manager|${meKey}`) : undefined} />
        ))}
      </div>
      <h3 className="text-[15px] font-bold mt-6 mb-2">Your people this week</h3>
      <Table
        rows={reports}
        keyOf={(p) => p.person_key}
        empty="No direct reports on record"
        cols={[
          { h: "Person", cell: (p) => <><span className="font-semibold">{p.full_name ?? p.person_key}</span><span className="sub">{p.title ?? ""}</span></> },
          ...personKeys.map((k) => ({
            h: title(k).replace(" Pct", " %"),
            w: "120px",
            cell: (p: { person_key: string }) => {
              const e = people.get(`${k}|person|${p.person_key}`);
              if (!e) return <span className="text-muted">—</span>;
              const t = toneFor(k, e.latest.value);
              const v = `${e.latest.value.toLocaleString(undefined, { maximumFractionDigits: 1 })}${unitOf(defs.data, k) === "pct" ? "%" : ""}`;
              return t === "neutral" ? <span>{v}</span> : <Chip tone={t}>{v}</Chip>;
            },
          })),
          { h: "Open work", cell: (p) => <span className="text-muted">{work.data?.filter((w) => w.owner_person_key === p.person_key).length ?? "—"}</span>, w: "90px" },
          { h: "Status", cell: (p) => <Chip tone={statusTone(p.status)}>{title(p.status)}</Chip>, w: "100px" },
        ]}
      />
    </Page>
  );
}
